import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Label,
  LineChart,
  Line,
} from 'recharts';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  COLOR_BLUE_BLACK_LIGHT,
  COLOR_PRIMARY,
  COLOR_PRIMARY_DARK,
  COLOR_TOOLTIP_BACKGROUND,
  DEMO_DASHBOARD_FILTER_GRAPH_TOOLTIP_LTM_DATA,
  exchangeRates,
  shortMonthNames,
} from 'src/const';
import {
  abbreviateNumber,
  checkUserTailorMarketAccess,
  convertCurrency,
  formatCurrency,
  getExampleListingsWhichMatchesSpecificCriteria,
  getPercentage,
  trackConfigurableOverviewGraphActivation,
  trackDistributionDataActivation,
} from 'src/utils/functions';
import { LoadingCard } from 'src/App/HOC';
import {
  COLOR_GREY_200,
  COLOR_GREY_400,
  COLOR_GREY_600,
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_2,
  COLOR_SECONDARY_1_600,
  COLOR_SECONDARY_2_600,
  COLOR_SECONDARY_2_700,
  COLOR_TERTIARY_1_600,
} from 'src/colors';
import { InfoTooltip } from 'src/App/HOC/Tooltips';
import { infoIconButton, infoIconStyle } from 'src/App/Dashboards/Market/styles/style';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FilteredListingsDashboardDataType } from 'src/interfaces/idashboard';
import { User } from 'src/interfaces/iuser';
import SimplifiedAirbnbPointerBody from 'src/App/HOC/Cards/SimplifiedAirbnbPointerBody';
import { useUpgradeMesageProFeature } from 'src/hooks/useUpgradeText';
import ShowMoreButton from 'src/App/Components/Buttons/ShowMoreButton';
import { logAmplitudeEvent } from 'src/utils/analytics';

const FilterChip = ({
  title,
  isActive,
  color,
  handleOnClick,
}: {
  title: string;
  isActive: boolean;
  color?: string;
  handleOnClick?: () => void;
}) => {
  color = !isActive ? '#999' : color ? color : COLOR_BLUE_BLACK_LIGHT;

  const style = {
    chip: {
      backgroundColor: isActive ? color : '#fff',
      borderColor: color,
      color: isActive ? '#fff' : color,
    },
    icon: {
      color: isActive ? '#fff' : color,
    },
  };

  return (
    <>
      <Chip
        label={title}
        variant={isActive ? 'filled' : 'outlined'}
        size="small"
        deleteIcon={
          isActive ? (
            <RemoveCircleOutlineRoundedIcon style={style.icon} />
          ) : (
            <AddCircleOutlineRoundedIcon style={style.icon} />
          )
        }
        onClick={() => handleOnClick && handleOnClick()}
        onDelete={() => handleOnClick && handleOnClick()}
        sx={style.chip}
      />
    </>
  );
};

type CatagoryMapType = { [key: string]: { name: string } };
type OptionalCatagoryMapType = { [key: string]: { name: string; isActive: boolean } };

const processDynamicRangeCatagoryMap = (
  rangeObject: any,
  currency: string,
  isCurrency?: boolean
) => {
  const resultObject: CatagoryMapType = {};
  for (const key in rangeObject) {
    resultObject[key] = {
      name: !isCurrency
        ? key
        : key
            .split('-')
            .map(
              data =>
                exchangeRates[currency].symbol +
                abbreviateNumber(convertCurrency(currency, Number(data)), true)
            )
            .join(' - '),
    };
  }
  return resultObject;
};

const PremiumOverlay = ({
  isXAxisTickVertical,
  handleClick,
  offsetX,
  offsetY,
  offsetWidth,
  offsetHeight,
  user,
  userTailorMarketAccess,
}: {
  isXAxisTickVertical: boolean | undefined;
  handleClick: any;
  offsetX?: number;
  offsetY?: number;
  offsetWidth?: number;
  offsetHeight?: number;
  user: User;
  userTailorMarketAccess: boolean;
}) => {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: offsetY ? offsetY + 'px' : 0,
        left: offsetX ? offsetX + 'px' : '30px',
        width: `calc(100% - 30px + ${offsetWidth ? offsetWidth + 'px' : '0px'})`,
        height: `calc(${isXAxisTickVertical ? '70%' : '80%'} + ${
          offsetHeight ? offsetHeight + 'px' : '0px'
        })`,
        background: '#fffa',
        backgroundBlendMode: 'multiply',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Typography
        variant="body1"
        onClick={() => handleClick('expert')}
        sx={{
          fontSize: '20px',
          fontWeight: 500,
          color: COLOR_PRIMARY_2,
          cursor: 'pointer',
          pointerEvents: 'auto',
          textDecoration: 'underline',
        }}
      >
        {useUpgradeMesageProFeature()}
      </Typography>
    </Box>
  );
};

const processCurrencyGraphLabelRange = (label: string, currency: string) => {
  return label
    .split('-')
    .map(
      valueUSD =>
        exchangeRates[currency].symbol +
        abbreviateNumber(convertCurrency(currency, Number(valueUSD)))
    )
    .join('-');
};

const MainChartTooltip = props => {
  const { active, payload, title, metricNameMap, currency } = props;

  useEffect(() => {
    if (active) {
      logAmplitudeEvent('tooltip_interacted', {
        tooltip_type: 'graph',
        parent: `filter_bar_chart`,
      });
    }
  }, [active]);

  if (active && payload && payload.length > 0) {
    const _payload = payload[0].payload;
    const unit = payload[0].unit;
    const barColor = payload[0].color;

    const isCurrency = unit !== '%';

    return (
      <Card sx={{ boxShadow: `0px 0px 10px 0px ${COLOR_GREY_600}` }}>
        <CardContent style={{ background: '#fff' }}>
          <Typography variant="body1" style={{ fontWeight: 'bolder', color: COLOR_GREY_600 }}>
            {payload[0].payload.timePeriod}
          </Typography>
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            <span style={{ color: COLOR_GREY_600 }}>{metricNameMap[title]}:</span>{' '}
            <span style={{ color: barColor }}>
              {isCurrency && unit}
              {isCurrency
                ? formatCurrency(convertCurrency(currency, _payload[title]).toFixed(0))
                : _payload[title]}
              {!isCurrency && unit}
            </span>
          </Typography>
        </CardContent>
      </Card>
    );
  }
  return null;
};

const ConfigurableFilterBarCard = ({
  filteredListingsData,
  cachedMonthlyLTMData,
  isLoading,
  currency,
  isDisabled,
  handleOnSampleClick,
  listings,
  setTopPerformingListingModalOpen,
  setOpenFullView,
  matches,
  avgMarketRevenue,
  avgMarketOccupancyRate,
  avgMarketAdr,
  cachedMarketBedroomADR,
}: {
  filteredListingsData: FilteredListingsDashboardDataType;
  cachedMonthlyLTMData: any;
  isLoading: boolean;
  currency: string;
  isDisabled: boolean;
  handleOnSampleClick: any;
  listings: any;
  setTopPerformingListingModalOpen: any;
  setOpenFullView: any;
  matches: boolean;
  avgMarketRevenue: number;
  avgMarketOccupancyRate: number;
  avgMarketAdr: number;
  cachedMarketBedroomADR: any;
}) => {
  const user: User = useSelector(state => (state as any).dashboard.user);
  const search = useSelector(state => (state as any).dashboard.search);
  const cachedMarketData = useSelector(state => (state as any).dashboard.cachedMarketData);
  let userTailorMarketAccess = checkUserTailorMarketAccess(user, search);
  const [metricsByBedroom, setMetricsByBedroom] = useState<any>(null);

  useEffect(() => {
    if (!cachedMarketData.loading && cachedMarketData.data && filteredListingsData) {
      const { adr_breakdown, occupancy_breakdown } = cachedMarketData.data;
      const adrByBedroom = adr_breakdown?.bedroom;
      const occupancyByBedroom = occupancy_breakdown?.bedroom;
      const revenueByBedroom = cachedMarketData.data.revenue_breakdown?.bedroom;

      if (adrByBedroom && occupancyByBedroom) {
        const updatedPropertySize = { ...filteredListingsData.propertySize };

        Object.keys(updatedPropertySize).forEach(bedroom => {
          const bedroomKey = bedroom === 'studio' ? '0' : bedroom === '6+' ? '6' : bedroom;

          if (adrByBedroom[bedroomKey]) {
            updatedPropertySize[bedroom]['adr'] = adrByBedroom[bedroomKey];
          }
          if (occupancyByBedroom[bedroomKey]) {
            updatedPropertySize[bedroom]['occupancyRate'] = occupancyByBedroom[bedroomKey];
          }
          if (revenueByBedroom[bedroomKey]) {
            updatedPropertySize[bedroom]['revenue'] = revenueByBedroom[bedroomKey];
          }
        });

        setMetricsByBedroom(updatedPropertySize);
      }
    }
  }, [cachedMarketData, filteredListingsData]);

  const graphConfig = {
    bedrooms: {
      data: metricsByBedroom,
      distributionData: filteredListingsData?.propertySizeMetricsDistribution,
      title: 'Bedrooms',
      tooltipText: 'See what is the best number of bedrooms for Airbnb in this market.',
      metricTabs: ['occupancyRate', 'adr', 'revenue'],
      catagoryMap: {
        studio: { name: 'Studio' },
        '1': { name: '1' },
        '2': { name: '2' },
        '3': { name: '3' },
        '4': { name: '4' },
        '5': { name: '5' },
        '6+': { name: '6+' },
      },
      optionalCatagoryMap: null,
      color: COLOR_SECONDARY_2_700,
      isXaxisCurrency: false,
      xAxisTitle: 'Bedrooms',
      maxBarSize: 13,
      showSelectionChips: false,
      isGraphSizeSmall: true,
      showLTMGraphTooltip: true,
      dynamicTooltipCatagory: null,
      graphTooltipLTMData: cachedMonthlyLTMData ? cachedMonthlyLTMData.bedroom : null,
      isGraphLocked: isDisabled,
      tooltipTitleSuffix: 'Bedroom',
      titleHighlightType: 'prefix',
      xAxisOrder: ['studio', '1', '2', '3', '4', '5', '6+'],
      isXAxisTickVertical: false,
    },
    propertyType: {
      data: filteredListingsData?.propertyType,
      distributionData: filteredListingsData?.propertyTypeMetricsDistribution,
      title: 'Property Type',
      tooltipText: 'See what property type is most in demand in this market.',
      metricTabs: ['occupancyRate', 'adr', 'revenue'],
      catagoryMap: {
        entire_home: { name: 'Entire Home' },
        private_room: { name: 'Private Room' },
      },
      optionalCatagoryMap: {
        condo: { name: 'Apartment / Condo', isActive: true },
        hotel_room: { name: 'Hotel Room', isActive: true },
        shared_room: { name: 'Shared Room', isActive: true },
      },
      color: COLOR_PRIMARY,
      isXaxisCurrency: false,
      xAxisTitle: null,
      maxBarSize: 13,
      showSelectionChips: true,
      isGraphSizeSmall: true,
      showLTMGraphTooltip: true,
      dynamicTooltipCatagory: null,
      graphTooltipLTMData: cachedMonthlyLTMData ? cachedMonthlyLTMData.property_type : null,
      isGraphLocked: false,
      tooltipTitleSuffix: null,
      titleHighlightType: 'prefix',
      xAxisOrder: null,
      isXAxisTickVertical: false,
    },
    bathrooms: {
      data: filteredListingsData?.bathroomNumber,
      distributionData: filteredListingsData?.bathroomNumberMetricsDistribution,
      title: 'Bathrooms',
      tooltipText: 'See how the number of bathrooms of a property impacts its performance.',
      metricTabs: ['occupancyRate'],
      catagoryMap: {
        studio: { name: 'Studio' },
        '1': { name: '1' },
        '2': { name: '2' },
        '3': { name: '3' },
        '4': { name: '4' },
        '5': { name: '5' },
        '6+': { name: '6+' },
      },
      optionalCatagoryMap: null,
      color: COLOR_SECONDARY_1_600,
      isXaxisCurrency: false,
      xAxisTitle: 'Bathrooms',
      maxBarSize: 13,
      showSelectionChips: false,
      isGraphSizeSmall: true,
      showLTMGraphTooltip: false,
      dynamicTooltipCatagory: null,
      graphTooltipLTMData: null,
      isGraphLocked: isDisabled,
      tooltipTitleSuffix: 'Bathrooms',
      titleHighlightType: 'suffix',
      xAxisOrder: null,
      isXAxisTickVertical: false,
    },
    adr: {
      data: filteredListingsData?.adrRange,
      distributionData: filteredListingsData?.adrRangeMetricsDitribution,
      title: 'Average Daily Rate',
      tooltipText: 'See how the average daily rate of a property impacts its performance.',
      metricTabs: ['occupancyRate'],
      catagoryMap: 'dynamicRange',
      optionalCatagoryMap: null,
      color: COLOR_SECONDARY_2_600,
      isXaxisCurrency: true,
      xAxisTitle: null,
      maxBarSize: 13,
      showSelectionChips: false,
      isGraphSizeSmall: true,
      showLTMGraphTooltip: false,
      dynamicTooltipCatagory: ['Budget', 'Economy', 'Midscale', 'Upscale', 'Luxury'],
      graphTooltipLTMData: null,
      isGraphLocked: isDisabled,
      tooltipTitleSuffix: null,
      titleHighlightType: 'suffix',
      xAxisOrder: null,
      isXAxisTickVertical: false,
    },
    rating: {
      data: filteredListingsData?.ratingRange,
      distributionData: filteredListingsData?.ratingRangeMetricsDistribution,
      title: 'Ratings',
      tooltipText: 'See how the review rating of a property impacts its occupancy rate',
      metricTabs: ['occupancyRate'],
      catagoryMap: {
        '0-4': { name: 'below 4' },
        '4-4.3': { name: '4-4.3' },
        '4.3-4.6': { name: '4.3-4.6' },
        '4.6-4.8': { name: '4.6- 4.8' },
        '4.8-5.1': { name: '4.8-5' },
      },
      optionalCatagoryMap: null,
      color: COLOR_PRIMARY,
      isXaxisCurrency: false,
      xAxisTitle: 'ratings out of 5★',
      maxBarSize: 13,
      showSelectionChips: false,
      isGraphSizeSmall: true,
      showLTMGraphTooltip: false,
      dynamicTooltipCatagory: null,
      graphTooltipLTMData: null,
      isGraphLocked: isDisabled,
      tooltipTitleSuffix: '★ ratings',
      titleHighlightType: 'suffix',
      xAxisOrder: null,
      isXAxisTickVertical: false,
    },
    hostListingCount: {
      data: filteredListingsData?.listingsPerHost,
      distributionData: filteredListingsData?.listingsPerHostMetricsDistribution,
      title: 'Listings per host',
      tooltipText: 'See how the number of listings owned by a host impacts its performanmce',
      metricTabs: ['occupancyRate'],
      catagoryMap: {
        '1': { name: '1' },
        '2-5': { name: '2-5' },
        '6-20': { name: '6-20' },
        '21+': { name: '21+' },
      },
      optionalCatagoryMap: null,
      color: COLOR_PRIMARY,
      isXaxisCurrency: false,
      xAxisTitle: 'listings',
      maxBarSize: 13,
      showSelectionChips: false,
      isGraphSizeSmall: true,
      showLTMGraphTooltip: false,
      dynamicTooltipCatagory: null,
      graphTooltipLTMData: null,
      isGraphLocked: isDisabled,
      tooltipTitleSuffix: 'Listings',
      titleHighlightType: 'prefix',
      xAxisOrder: null,
      isXAxisTickVertical: false,
    },
    minimumNights: {
      data: filteredListingsData?.minNightRange,
      distributionData: filteredListingsData?.minNightRangeMetricsDistribution,
      title: 'Minimum Nights',
      tooltipText: 'See how the properties with different minimum nights impact their performance.',
      metricTabs: ['occupancyRate'],
      catagoryMap: {
        '1-6': { name: '1-6' },
        '7-29': { name: '7-29' },
        '30-999': { name: '30+' },
      },
      optionalCatagoryMap: null,
      color: COLOR_TERTIARY_1_600,
      isXaxisCurrency: false,
      xAxisTitle: 'nights',
      maxBarSize: 13,
      showSelectionChips: false,
      isGraphSizeSmall: true,
      showLTMGraphTooltip: false,
      dynamicTooltipCatagory: null,
      graphTooltipLTMData: null,
      isGraphLocked: isDisabled,
      tooltipTitleSuffix: 'Minimum nights',
      titleHighlightType: 'prefix',
      xAxisOrder: null,
      isXAxisTickVertical: false,
    },
  };

  const [tabSelected, setTabSelected] = useState<'occupancyRate' | 'adr' | 'revenue' | 'supply'>(
    'revenue'
  );
  const [filterType, setFilterType] = useState<
    | 'bedrooms'
    | 'propertyType'
    | 'bathrooms'
    | 'adr'
    | 'rating'
    | 'hostListingCount'
    | 'minimumNights'
  >('bedrooms');

  const [catagoryMapState, setCatagoryMapState] = useState<CatagoryMapType>(
    graphConfig[filterType].catagoryMap === 'dynamicRange'
      ? {}
      : (graphConfig[filterType].catagoryMap as CatagoryMapType)
  );
  const [
    optionalCatagoryMapState,
    setOptionalCatagoryMapState,
  ] = useState<OptionalCatagoryMapType | null>(graphConfig[filterType].optionalCatagoryMap);

  const [currentBarChartDataOnHover, setCurrentBarChartDataOnHover] = useState<any>(null);

  const [isLTMTooltipHovered, setIsLTMTooltipHovered] = useState<boolean>(false);

  const [showRevenueBreakdown, setShowRevenueBreakdown] = useState<boolean>(false);

  useEffect(() => {
    if (isDisabled) {
      setFilterType('propertyType');
    } else {
      setFilterType('bedrooms');
    }
  }, [isDisabled]);

  useEffect(() => {
    if (graphConfig[filterType].catagoryMap === 'dynamicRange') {
      setCatagoryMapState(
        processDynamicRangeCatagoryMap(
          graphConfig[filterType].data,
          currency,
          graphConfig[filterType].isXaxisCurrency
        )
      );
    } else {
      setCatagoryMapState(graphConfig[filterType].catagoryMap as CatagoryMapType);
    }
    setOptionalCatagoryMapState(graphConfig[filterType].optionalCatagoryMap);

    if (graphConfig[filterType].metricTabs.length == 1) {
      setTabSelected('occupancyRate');
    }
  }, [currency, filterType]);

  useEffect(() => {
    !currentBarChartDataOnHover && setIsLTMTooltipHovered(false);
  }, [currentBarChartDataOnHover]);

  const processGraphData = (dataObj: any) => {
    const graphResultArray: { [key: string]: string | number }[] = [];

    const dataToUse = graphConfig[filterType].xAxisOrder
      ? graphConfig[filterType].xAxisOrder
      : Object.keys(dataObj);

    dataToUse &&
      dataToUse.forEach(key => {
        catagoryMapState[key] &&
          graphResultArray.push({
            ...dataObj[key],
            name: key,
          });

        optionalCatagoryMapState &&
          optionalCatagoryMapState[key]?.isActive &&
          graphResultArray.push({
            ...dataObj[key],
            name: key,
            isOptionalKey: true,
          });
      });

    return graphResultArray;
  };

  const processDistGraphData = (dataObj: any) => {
    const graphResultArray: { [key: string]: string | number }[] = [];

    dataObj &&
      Object.keys(dataObj).forEach(key => {
        graphResultArray.push({
          name: key,
          frequency: dataObj[key],
        });
      });

    return graphResultArray;
  };

  const handleFilterChipClick = (valueClicked: string) => {
    optionalCatagoryMapState &&
      setOptionalCatagoryMapState(prevState => {
        if (!prevState) return prevState;

        const prevStateClone = { ...prevState };
        prevStateClone[valueClicked].isActive = prevState[valueClicked].isActive ? false : true;
        return prevStateClone;
      });
  };

  const getTotalListingsInTheDistributionGraph = (data: { [key: string]: number }) => {
    let totalListings = 0;

    if (!data) return totalListings;

    for (let listingCount of Object.values(data)) {
      totalListings += listingCount;
    }
    return totalListings;
  };

  const getAvgMarketStatsOnTabSelected = () => {
    const marketAvgTabMap = {
      occupancyRate: avgMarketOccupancyRate,
      revenue: avgMarketRevenue,
      adr: avgMarketAdr,
    };
    return marketAvgTabMap[tabSelected] ? marketAvgTabMap[tabSelected] : null;
  };

  const metricNameMap = {
    occupancyRate: 'Occupancy Rate',
    adr: 'Daily Rate',
    revenue: 'Revenue',
  };

  const filterTypeMap = {
    bedrooms: 'Bedrooms',
    propertyType: 'Property Type',
    bathrooms: 'Bathrooms',
    minimumNights: 'Minimum Stay',
    adr: 'Price Tier',
    hostListingCount: 'Host Portfolio',
    rating: 'Star Rating',
  };

  const isTabSelectedDataCurrency = tabSelected === 'adr' || tabSelected === 'revenue';

  const processDataString = (dateStr: string) => {
    const [year, month] = dateStr.split('-').map(Number);
    return shortMonthNames[month - 1] + ', ' + (year % 100);
  };

  const getFormattedDistGraphTick = tick => {
    const isCurrency = tabSelected != 'occupancyRate';
    let [start, end] = tick.split(' - ').map(Number);

    if (isCurrency) {
      return `${exchangeRates[currency].symbol}${abbreviateNumber(
        convertCurrency(currency, start)
      )}-${exchangeRates[currency].symbol}${abbreviateNumber(convertCurrency(currency, end))}`;
    } else {
      return `${start}-${end}%`;
    }
  };

  const ConfigSelect = ({
    value,
    setValue,
    optionsMap,
    customStyle,
    activeColor,
  }: {
    value: string;
    setValue: React.Dispatch<React.SetStateAction<any>>;
    optionsMap: { [index: string]: string };
    customStyle?: any;
    activeColor: string;
  }) => (
    <FormControl variant="standard" sx={{ m: 0, minWidth: 120 }}>
      <Select
        value={value}
        onChange={event => {
          logAmplitudeEvent('filter_bar_chart_configured');
          setValue(event.target.value);
          trackConfigurableOverviewGraphActivation();
          closeCustomTooltip();
        }}
        MenuProps={{
          autoFocus: false,
        }}
        sx={{
          fontWeight: 500,
          paddingTop: 0,
          '& #demo-simple-select-standard': {
            paddingTop: '0px !important',
            paddingLeft: '5px',
          },
          '& .MuiSelect-standard': {
            paddingTop: '0px !important',
            backgroundColor: 'transparent',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
            outline: 'none',
          },
          fieldset: {
            border: 'none !important',
            outline: 'none',
          },
          ...customStyle,
        }}
        renderValue={selected => (
          <Typography variant="body1" sx={{ fontWeight: 500, color: activeColor }}>
            {optionsMap[selected]}
          </Typography>
        )}
      >
        {Object.keys(optionsMap).map(option => (
          <MenuItem value={option}>{optionsMap[option]}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const LTMTooltip = props => {
    const { active, payload, label } = props;
    if (active && payload && payload.length > 0) {
      // Customize the content of the tooltip
      const { date, metric } = payload[0].payload;

      return (
        <Box
          sx={{
            position: 'relative',
            maxWidth: '300px',
            minWidth: '180px',
            background: '#fff',
            borderRadius: '4px',
            boxShadow: '0 2px 8px #0004',
            padding: '10px',
            pointerEvents: 'auto',
            top: '-75px',
            fontWeight: 500,
            color: COLOR_GREY_600,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: graphConfig[filterType].color, fontWeight: 'inherit' }}
          >
            <span style={{ color: COLOR_GREY_600 }}>{processDataString(date)}: </span>
            {isTabSelectedDataCurrency && exchangeRates[currency].symbol}
            {abbreviateNumber(metric)}
            {!isTabSelectedDataCurrency && '%'}
          </Typography>
        </Box>
      );
    }

    return <></>;
  };

  const DistributionTooltip = props => {
    const { active, payload, label } = props;
    if (active && payload && payload.length > 0) {
      const isDistributionDataActivationTracked = localStorage.getItem(
        'distributionDataActivationTracked'
      );
      if (!isDistributionDataActivationTracked) {
        trackDistributionDataActivation();
        localStorage.setItem('distributionDataActivationTracked', 'true');
      }
      // Customize the content of the tooltip
      const { name, frequency } = payload[0].payload;

      return (
        <Box
          sx={{
            position: 'relative',
            minWidth: '180px',
            maxWidth: '300px',
            background: '#fff',
            borderRadius: '4px',
            boxShadow: '0 2px 8px #0004',
            padding: '10px',
            pointerEvents: 'auto',
            top: '-75px',
            fontWeight: 500,
            color: COLOR_GREY_600,
          }}
        >
          <Typography variant="body2" sx={{ marginBottom: '5px', fontWeight: 'inherit' }}>
            {getFormattedDistGraphTick(name)}:{' '}
            <span style={{ color: graphConfig[filterType].color }}>{frequency} listings</span>
          </Typography>
        </Box>
      );
    }

    return <></>;
  };

  const closeCustomTooltip = () => {
    setCurrentBarChartDataOnHover(null);
    setShowRevenueBreakdown(false);
  };

  const CustomMainChartTooltipContent = props => {
    try {
      const {
        active,
        payload,
        label,
        isRenderedFromOutside,
        handleMouseOut,
        userTailorMarketAccess,
      } = props;
      const tooltipTitle = graphConfig[filterType].title;
      const showExampleListingSection =
        tabSelected === 'occupancyRate' || tabSelected === 'adr' || tabSelected === 'revenue';

      let value = payload?.[0]?.[tabSelected] ?? payload?.[0]?.payload?.[tabSelected] ?? 0;
      const [exampleListingsThatMatchesTheCriteria, listingMatchesTheExactValue] =
        showExampleListingSection && value && tooltipTitle && label
          ? getExampleListingsWhichMatchesSpecificCriteria(
              listings.data,
              tabSelected,
              value,
              3,
              tooltipTitle,
              label
            )
          : [];

      const tabSelectedLTMMetricsMap = {
        occupancyRate: 'occupancy_rate_50',
        adr: 'adr_50',
        revenue: 'revenue_50',
      };

      const labelGraphTypeMap = {
        studio: 0,
        '6+': 6,
      };

      const processLabelName = (label: string) => {
        return labelGraphTypeMap[label] != undefined ? labelGraphTypeMap[label] : label;
      };

      const findMaxMinGraphPoint = (
        formattedGraphArray: { date: string; metric: number }[] | null | undefined
      ) => {
        if (!formattedGraphArray) return { high: -1, low: -1 };

        let max = -Infinity;
        let min = Infinity;

        formattedGraphArray.forEach(graphData => {
          max = Math.max(max, graphData.metric);
          min = Math.min(min, graphData.metric);
        });

        return { high: max, low: min };
      };

      if (active && payload && payload.length > 0) {
        // Customize the content of the tooltip
        const { dataKey, value, unit } = payload[0];

        const formattedGraphArray =
          !graphConfig[filterType].isGraphLocked &&
          isDisabled &&
          graphConfig[filterType].showLTMGraphTooltip
            ? DEMO_DASHBOARD_FILTER_GRAPH_TOOLTIP_LTM_DATA[tabSelected]
            : graphConfig[filterType].graphTooltipLTMData
            ? Object.keys(graphConfig[filterType].graphTooltipLTMData[processLabelName(label)]).map(
                date => {
                  return {
                    date: date,
                    metric:
                      graphConfig[filterType].graphTooltipLTMData[processLabelName(label)][date][
                        tabSelectedLTMMetricsMap[tabSelected]
                      ],
                  };
                }
              )
            : [];

        if (isTabSelectedDataCurrency) {
          formattedGraphArray.forEach(Val => {
            Val['metric'] = convertCurrency(currency, Val['metric']);
          });
        }

        const { high, low } = findMaxMinGraphPoint(formattedGraphArray);

        const CustomizedDot = props => {
          const { cx, cy, stroke, payload, value } = props;
          const size = 6;

          if (value !== high && value !== low) return <></>;

          return (
            <svg x={cx - size} y={cy - size} width={size * 2} height={size * 2} fill="white">
              <g transform={`translate(${size} ${size})`}>
                <circle r={size} fill={graphConfig[filterType].color} />
                <circle r={size / 2} fill="white" />
              </g>
            </svg>
          );
        };

        return (
          <>
            {!showRevenueBreakdown && (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  flexDirection: 'row-reverse',
                }}
              >
                <ShowMoreButton
                  isShowingLess={true}
                  handleButtonClick={() => {
                    logAmplitudeEvent('filter_bar_chart_details_viewed');
                    setShowRevenueBreakdown(true);
                  }}
                />
              </Box>
            )}
            <Box
              onMouseOut={handleMouseOut && handleMouseOut}
              onMouseEnter={() => {
                setIsLTMTooltipHovered(_ => true);
              }}
              sx={{
                ...{
                  position: 'relative',
                  width: '100%',
                  background: '#fff',
                  borderRadius: '8px',
                  padding: '20px',
                  overflowY: showExampleListingSection ? 'auto' : 'hidden',
                  boxShadow: '0 -2px 10px -5px #0003',
                  marginTop: '20px',
                  borderTop: `2px solid ${COLOR_GREY_400}`,
                  display: showRevenueBreakdown ? 'block' : 'none',
                },
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '900px',
                  background: COLOR_GREY_200,
                  color: '#000',
                  cursor: 'pointer',
                  transition: 'background 0.25s ease',
                  '&:hover': {
                    background: COLOR_GREY_400,
                  },
                }}
                onClick={closeCustomTooltip}
              >
                <CloseRoundedIcon />
              </Box>
              <Grid container>
                <Grid item>
                  <Typography variant="h5" sx={{ fontSize: '14px' }}>
                    {graphConfig[filterType].catagoryMap !== 'dynamicRange'
                      ? graphConfig[filterType].catagoryMap[label]?.name
                        ? graphConfig[filterType].catagoryMap[label]?.name
                        : optionalCatagoryMapState
                        ? optionalCatagoryMapState[label]?.name
                        : ''
                      : graphConfig[filterType].title === 'Average Daily Rate'
                      ? processCurrencyGraphLabelRange(label, currency)
                      : label}{' '}
                    {graphConfig[filterType].tooltipTitleSuffix &&
                      graphConfig[filterType].tooltipTitleSuffix}
                  </Typography>
                </Grid>

                {!isRenderedFromOutside && (
                  <Grid container item xs={12}>
                    {isTabSelectedDataCurrency ? (
                      <Typography variant="h5" sx={{ fontSize: '15px', marginTop: '6px' }}>
                        <span style={{ fontWeight: 'bold', color: graphConfig[filterType].color }}>
                          {metricNameMap[dataKey]}:{' '}
                        </span>
                        {unit} {formatCurrency(convertCurrency(currency, value).toString())}
                      </Typography>
                    ) : (
                      <Typography variant="h5" sx={{ fontSize: '15px', marginTop: '6px' }}>
                        <span style={{ fontWeight: 'bold', color: graphConfig[filterType].color }}>
                          {metricNameMap[dataKey]}:{' '}
                        </span>
                        {value} {unit}
                      </Typography>
                    )}
                  </Grid>
                )}

                {isRenderedFromOutside && (
                  <Grid container item xs={12}>
                    <Typography variant="h5" sx={{ fontSize: '15px', marginTop: '6px' }}>
                      <span style={{ fontWeight: 'bold', color: graphConfig[filterType].color }}>
                        {metricNameMap[tabSelected]}:
                      </span>{' '}
                      {isTabSelectedDataCurrency && exchangeRates[currency].symbol}
                      {formatCurrency(
                        isTabSelectedDataCurrency
                          ? convertCurrency(currency, payload[0][tabSelected]) + ''
                          : payload[0][tabSelected] + ''
                      )}
                      {!isTabSelectedDataCurrency && '%'}
                      {value}
                    </Typography>
                  </Grid>
                )}

                <Grid
                  container
                  item
                  xs={12}
                  md={6}
                  spacing={4}
                  flexDirection={'column'}
                  sx={{ maxHeight: '400px' }}
                >
                  <Grid container item xs={12}>
                    <Grid container item xs={12}>
                      <Grid container item xs={12}>
                        <Typography
                          variant="h5"
                          sx={{ fontSize: '15px', marginTop: '10px', fontWeight: 'bold' }}
                        >
                          Distribution
                        </Typography>
                      </Grid>
                      <Grid
                        container
                        item
                        xs={12}
                        sx={{ justifyContent: matches ? 'flex-start' : 'flex-end' }}
                      >
                        <Typography
                          variant="h5"
                          sx={{ fontSize: '15px', marginTop: '10px', fontWeight: 'bold' }}
                        >
                          Total Listings:{' '}
                          {formatCurrency(
                            getTotalListingsInTheDistributionGraph(
                              graphConfig[filterType].distributionData![label][tabSelected]
                            ) + ''
                          )}
                        </Typography>
                      </Grid>
                      {graphConfig[filterType].distributionData && (
                        <Grid
                          container
                          item
                          xs={12}
                          sx={{ marginTop: '10px' }}
                          id={'filter-graph-container' + graphConfig[filterType].title}
                        >
                          <>
                            <ResponsiveContainer
                              width={'100%'}
                              height={150}
                              style={{ overflow: 'hidden' }}
                            >
                              <BarChart
                                data={processDistGraphData(
                                  graphConfig[filterType].distributionData![label][tabSelected]
                                )}
                                maxBarSize={
                                  graphConfig[filterType].maxBarSize
                                    ? graphConfig[filterType].maxBarSize
                                    : 20
                                }
                                margin={{ top: 10, right: 15, left: -20, bottom: 35 }}
                                style={{
                                  fontFamily: 'Roboto',
                                  position: 'relative',
                                }}
                              >
                                {!graphConfig[filterType].isGraphSizeSmall ? (
                                  <CartesianGrid strokeDasharray="3 3" />
                                ) : (
                                  <CartesianGrid
                                    vertical={false}
                                    horizontal={true}
                                    stroke={COLOR_GREY_400}
                                    strokeDasharray="6 6"
                                  />
                                )}
                                <XAxis
                                  dataKey="name"
                                  tickLine={false}
                                  axisLine={graphConfig[filterType].isGraphSizeSmall ? false : true}
                                  angle={graphConfig[filterType].isXAxisTickVertical ? -90 : 0}
                                  textAnchor={
                                    graphConfig[filterType].isXAxisTickVertical ? 'end' : 'middle'
                                  }
                                  style={{
                                    fontSize: graphConfig[filterType].isGraphSizeSmall
                                      ? '0.75rem'
                                      : '14px',
                                    fontFamily: 'Roboto',
                                  }}
                                  tickFormatter={props => getFormattedDistGraphTick(props)}
                                >
                                  <Label
                                    value={metricNameMap[tabSelected]}
                                    offset={-5}
                                    position="insideBottom"
                                    style={{
                                      fontSize: '0.75rem',
                                      fontFamily: 'Roboto',
                                    }}
                                    fill={COLOR_GREY_600}
                                  />
                                </XAxis>
                                <YAxis
                                  tickLine={false}
                                  axisLine={graphConfig[filterType].isGraphSizeSmall ? false : true}
                                  style={{
                                    fontSize: graphConfig[filterType].isGraphSizeSmall
                                      ? '0.75rem'
                                      : '14px',
                                    fontFamily: 'Roboto',
                                  }}
                                />
                                <Tooltip
                                  isAnimationActive={false}
                                  animationDuration={0}
                                  allowEscapeViewBox={{ x: false, y: false }}
                                  content={<DistributionTooltip />}
                                />
                                <Bar
                                  dataKey="frequency"
                                  fill={graphConfig[filterType].color}
                                  style={{ cursor: 'pointer' }}
                                  radius={[100, 100, 100, 100]}
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          </>
                        </Grid>
                      )}
                    </Grid>

                    <Grid container item xs={12}>
                      {graphConfig[filterType].showLTMGraphTooltip && (
                        <Grid container item xs={12}>
                          <Typography
                            variant="h5"
                            sx={{
                              fontSize: '15px',
                              marginTop: '10px',
                              fontWeight: 'bold',
                            }}
                          >
                            Monthly Trend (Seasonality)
                          </Typography>
                        </Grid>
                      )}

                      {(graphConfig[filterType].graphTooltipLTMData ||
                        (!graphConfig[filterType].isGraphLocked &&
                          isDisabled &&
                          graphConfig[filterType].showLTMGraphTooltip)) && (
                        <Grid
                          container
                          item
                          xs={12}
                          sx={{ marginTop: '10px', position: 'relative' }}
                        >
                          <ResponsiveContainer
                            width={'100%'}
                            height={150}
                            style={{ overflow: 'hidden' }}
                          >
                            <LineChart
                              data={
                                formattedGraphArray
                                  ? formattedGraphArray
                                  : graphConfig[filterType].data
                              }
                              margin={{ top: 10, right: 15, left: -20, bottom: 35 }}
                              style={{ fontFamily: 'Roboto' }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis
                                dataKey="date"
                                tickLine={false}
                                fontSize={12}
                                ticks={[
                                  formattedGraphArray[0].date,
                                  formattedGraphArray[formattedGraphArray.length - 1].date,
                                ]}
                                tickFormatter={props => processDataString(props)}
                                tickMargin={10}
                                tickAlign={'start'}
                              />
                              <YAxis
                                fontSize={12}
                                tickLine={false}
                                tickFormatter={props => abbreviateNumber(props, true)}
                              />
                              <Line
                                type="monotone"
                                dataKey={'metric'}
                                stroke={graphConfig[filterType].color}
                                strokeWidth={3}
                                dot={<CustomizedDot />}
                                isAnimationActive={false}
                              />
                              <Tooltip
                                isAnimationActive={false}
                                animationDuration={0}
                                allowEscapeViewBox={{ x: false, y: false }}
                                content={<LTMTooltip />}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                          {!graphConfig[filterType].isGraphLocked && isDisabled && (
                            <PremiumOverlay
                              isXAxisTickVertical={graphConfig[filterType].isXAxisTickVertical}
                              offsetX={-10}
                              offsetWidth={40}
                              handleClick={handleOnSampleClick}
                              user={user}
                              userTailorMarketAccess={userTailorMarketAccess}
                            />
                          )}
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item container xs={12} md={6} flexDirection={'column'}>
                  {showExampleListingSection && exampleListingsThatMatchesTheCriteria && (
                    <Grid container item xs={12}>
                      <Grid container justifyContent="center" item xs={12}>
                        <Typography
                          variant="h5"
                          sx={{
                            fontSize: '15px',
                            marginTop: '6px',
                            width: '100%',
                            minWidth: '320px',
                            maxWidth: '450px',
                          }}
                        >
                          Example listing with{' '}
                          <span
                            style={{ fontWeight: 'bold', color: graphConfig[filterType].color }}
                          >
                            {listingMatchesTheExactValue ? '' : 'Approx. '}
                            {metricNameMap[tabSelected]}:
                          </span>{' '}
                          {isTabSelectedDataCurrency && exchangeRates[currency].symbol}
                          {formatCurrency(
                            isTabSelectedDataCurrency
                              ? convertCurrency(currency, payload[0][tabSelected]) + ''
                              : payload[0][tabSelected] + ''
                          )}
                          {!isTabSelectedDataCurrency && '%'}
                        </Typography>
                      </Grid>
                      <Grid
                        container
                        item
                        xs={12}
                        direction="column"
                        flexWrap="nowrap"
                        justifyContent="flex-start"
                        alignItems="center"
                        sx={{
                          maxHeight: '700px',
                          marginTop: '10px',
                          paddingBottom: '10px',
                          position: 'relative',
                          width: '100%',
                          overflowX: 'auto',
                        }}
                      >
                        {exampleListingsThatMatchesTheCriteria.map(listing => {
                          const bedroom =
                            listing.bedrooms.toLowerCase() == 'studio' ? 0 : listing.bedrooms;
                          const referenceADR = cachedMarketBedroomADR
                            ? cachedMarketBedroomADR[bedroom]
                            : null;
                          return (
                            <Box
                              key={listing.listingID}
                              sx={{
                                width: '100%',
                                minWidth: '320px',
                                maxWidth: '450px',
                                padding: '20px',
                              }}
                            >
                              <SimplifiedAirbnbPointerBody
                                data={listing}
                                currency={currency}
                                canDelete={false}
                                isDisabled={isDisabled}
                                deleteListing={() => {}}
                                setOpenFullView={closeCustomTooltip}
                                isHidden={false}
                                isEstimator={false}
                                setTopPerformingListingOnFocus={() => {}}
                                setTopPerformingListingModalOpen={setTopPerformingListingModalOpen}
                                handleOnSampleClick={handleOnSampleClick}
                                showHighResListingImage={true}
                                referenceADR={referenceADR}
                              />
                            </Box>
                          );
                        })}
                        {isDisabled && (
                          <PremiumOverlay
                            isXAxisTickVertical={false}
                            offsetX={-10}
                            offsetWidth={220}
                            offsetHeight={80}
                            handleClick={() => {
                              handleOnSampleClick();
                              closeCustomTooltip();
                            }}
                            user={user}
                            userTailorMarketAccess={userTailorMarketAccess}
                          />
                        )}
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Box>
          </>
        );
      }
    } catch (err) {
      console.error(err);
    }
    return <></>;
  };

  const processedGraphData =
    filterType && graphConfig[filterType].data && processGraphData(graphConfig[filterType].data);

  useEffect(() => {
    !currentBarChartDataOnHover &&
      processedGraphData &&
      processedGraphData.length &&
      setCurrentBarChartDataOnHover({
        payload: processedGraphData[0],
      });
  }, [filterType, tabSelected, graphConfig]);

  return (
    <>
      <Grid
        container
        direction="column"
        justifyContent="center"
        item
        xs={12}
        spacing={1}
        sx={{ padding: '5px', boxShadow: '0 4px 10px #0003', borderRadius: 1 }}
      >
        <Grid container item>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex' }}>
              <Typography variant="body1" className="subHeadings">
                <ConfigSelect
                  value={tabSelected}
                  setValue={setTabSelected}
                  optionsMap={Object.keys(metricNameMap)
                    .filter(key => graphConfig[filterType].metricTabs.includes(key))
                    .reduce((obj, key) => {
                      obj[key] = metricNameMap[key];
                      return obj;
                    }, {})}
                  activeColor={COLOR_PRIMARY_2}
                />{' '}
                by{' '}
                <ConfigSelect
                  value={filterType}
                  setValue={setFilterType}
                  optionsMap={filterTypeMap}
                  customStyle={{ marginLeft: '5px' }}
                  activeColor={COLOR_PRIMARY_2}
                />{' '}
                <InfoTooltip
                  enterTouchDelay={0}
                  leaveTouchDelay={30000}
                  title={
                    <Card>
                      <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
                        <Typography
                          variant="body2"
                          style={{ fontWeight: 500, marginBottom: '5px' }}
                        >
                          {graphConfig[filterType].tooltipText}
                        </Typography>
                        <Grid container xs={12}>
                          <>
                            {catagoryMapState &&
                              graphConfig[filterType].catagoryMap === 'dynamicRange' &&
                              graphConfig[filterType].dynamicTooltipCatagory &&
                              Object.keys(catagoryMapState).map((catagoryMap, idx) => (
                                <Grid
                                  item
                                  xs={12}
                                  key={idx}
                                  direction={'row'}
                                  sx={{ marginBottom: '2px' }}
                                >
                                  <Typography variant="body2">
                                    <span style={{ fontWeight: 'bold' }}>
                                      {graphConfig[filterType].dynamicTooltipCatagory![idx] + ': '}
                                    </span>
                                    {processCurrencyGraphLabelRange(catagoryMap, currency)}
                                  </Typography>
                                </Grid>
                              ))}
                          </>
                        </Grid>
                      </CardContent>
                    </Card>
                  }
                  style={{ cursor: 'pointer' }}
                >
                  <IconButton style={infoIconButton}>
                    <InfoOutlinedIcon style={infoIconStyle} />
                  </IconButton>
                </InfoTooltip>
              </Typography>
            </Box>
          </Grid>

          {isLoading ? (
            <LoadingCard height={250} color={graphConfig[filterType].color} />
          ) : (
            <>
              {graphConfig[filterType].showSelectionChips && (
                <Grid xs={12} item container>
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    justifyContent="flex-start"
                    alignItems="center"
                    gap={'10px'}
                    sx={{ width: '100%', marginTop: '5px', marginBottom: '0px' }}
                  >
                    {optionalCatagoryMapState &&
                      Object.keys(optionalCatagoryMapState).map(catagory => (
                        <FilterChip
                          title={optionalCatagoryMapState[catagory].name}
                          isActive={optionalCatagoryMapState[catagory].isActive}
                          color={graphConfig[filterType].color}
                          handleOnClick={() => handleFilterChipClick(catagory)}
                        />
                      ))}
                  </Stack>
                </Grid>
              )}

              <Grid
                container
                item
                xs={12}
                sx={{ position: 'relative', paddingTop: '20px' }}
                id={'filter-graph-container' + graphConfig[filterType].title}
              >
                {processedGraphData && (
                  <>
                    <ResponsiveContainer
                      width="100%"
                      height={graphConfig[filterType].isGraphSizeSmall ? 180 : 250}
                    >
                      <BarChart
                        data={processedGraphData}
                        maxBarSize={
                          graphConfig[filterType].maxBarSize
                            ? graphConfig[filterType].maxBarSize
                            : 20
                        }
                        margin={{
                          left: tabSelected === 'revenue' ? 6 : tabSelected === 'adr' ? 0 : -15,
                          bottom: graphConfig[filterType].isXAxisTickVertical ? 40 : 10,
                        }}
                        style={{
                          fontFamily: 'Roboto',
                          position: 'relative',
                        }}
                        onMouseLeave={_ => {
                          setTimeout(() => {
                            setIsLTMTooltipHovered(prev => {
                              if (prev) {
                                return true;
                              } else {
                                return false;
                              }
                            });
                          }, 300);
                        }}
                      >
                        {!graphConfig[filterType].isGraphSizeSmall ? (
                          <CartesianGrid strokeDasharray="3 3" />
                        ) : (
                          <CartesianGrid
                            vertical={false}
                            horizontal={true}
                            stroke={COLOR_GREY_400}
                            strokeDasharray="6 6"
                          />
                        )}
                        <XAxis
                          dataKey="name"
                          tickFormatter={props =>
                            optionalCatagoryMapState && optionalCatagoryMapState[props]
                              ? optionalCatagoryMapState[props].name
                              : catagoryMapState[props].name
                          }
                          tickLine={false}
                          axisLine={graphConfig[filterType].isGraphSizeSmall ? false : true}
                          angle={graphConfig[filterType].isXAxisTickVertical ? -90 : 0}
                          textAnchor={
                            graphConfig[filterType].isXAxisTickVertical ? 'end' : 'middle'
                          }
                          style={{
                            fontSize: graphConfig[filterType].isGraphSizeSmall ? '0.75rem' : '14px',
                            fontFamily: 'Roboto',
                          }}
                        >
                          {graphConfig[filterType].xAxisTitle && (
                            <Label
                              value={graphConfig[filterType].xAxisTitle}
                              offset={-5}
                              position="insideBottom"
                              style={{
                                fontSize: '0.75rem',
                                fontFamily: 'Roboto',
                              }}
                              fill={COLOR_GREY_600}
                            />
                          )}
                        </XAxis>
                        <YAxis
                          tickLine={false}
                          axisLine={graphConfig[filterType].isGraphSizeSmall ? false : true}
                          tickFormatter={props =>
                            (isTabSelectedDataCurrency ? exchangeRates[currency].symbol : '') +
                            abbreviateNumber(
                              isTabSelectedDataCurrency ? convertCurrency(currency, props) : props,
                              true
                            ) +
                            (tabSelected === 'occupancyRate' ? '%' : '')
                          }
                          style={{
                            fontSize: graphConfig[filterType].isGraphSizeSmall ? '0.75rem' : '14px',
                            fontFamily: 'Roboto',
                          }}
                        />
                        <Bar
                          dataKey={tabSelected}
                          fill={COLOR_PRIMARY}
                          unit={
                            tabSelected === 'occupancyRate'
                              ? '%'
                              : tabSelected === 'supply'
                              ? 'listings'
                              : exchangeRates[currency].symbol
                          }
                          style={{ cursor: 'pointer' }}
                          radius={[100, 100, 100, 100]}
                          onMouseEnter={data => {
                            setCurrentBarChartDataOnHover({
                              payload: data.payload,
                            });
                          }}
                          onClick={data => {
                            setCurrentBarChartDataOnHover({
                              payload: data.payload,
                            });
                          }}
                        />
                        <Tooltip
                          cursor={{ fill: 'transparent' }}
                          content={
                            <MainChartTooltip
                              title={tabSelected}
                              metricNameMap={metricNameMap}
                              currency={currency}
                            />
                          }
                        />
                      </BarChart>
                    </ResponsiveContainer>
                    {graphConfig[filterType].isGraphLocked && (
                      <PremiumOverlay
                        isXAxisTickVertical={graphConfig[filterType].isXAxisTickVertical}
                        handleClick={handleOnSampleClick}
                        user={user}
                        userTailorMarketAccess={userTailorMarketAccess}
                      />
                    )}
                  </>
                )}
              </Grid>
            </>
          )}

          {currentBarChartDataOnHover &&
            CustomMainChartTooltipContent({
              active: true,
              payload: [currentBarChartDataOnHover.payload],
              label: currentBarChartDataOnHover.payload.name,
              isRenderedFromOutside: true,
              userTailorMarketAccess: userTailorMarketAccess,
            })}
        </Grid>
      </Grid>
    </>
  );
};

export default React.memo(ConfigurableFilterBarCard);
